import React, { useState, useEffect, useRef } from 'react';
import { Navigation, Autoplay } from 'swiper/modules';
import Swiper from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import { postAPI, getURL } from '../../../app/api';

// SVG Avatar Component - shown when member has no image
const AvatarIcon = ({ name, color }) => {
    const safeName = name?.replace(/\s/g, '') || 'default';
    return (
        <svg
            width="100%"
            height="100%"
            viewBox="0 0 200 200"
            style={{ display: 'block' }}
        >
            {/* Background circle with gradient */}
            <defs>
                <linearGradient id={`grad-${safeName}`} x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: color, stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: color, stopOpacity: 0.7 }} />
                </linearGradient>
            </defs>
            <circle cx="100" cy="100" r="95" fill={`url(#grad-${safeName})`} />

            {/* Person silhouette - head */}
            <circle cx="100" cy="70" r="30" fill="rgba(255,255,255,0.9)" />
            {/* Person silhouette - body */}
            <ellipse cx="100" cy="140" rx="45" ry="30" fill="rgba(255,255,255,0.9)" />
        </svg>
    );
};

// Generate random color for avatar based on name
const getAvatarColor = (name) => {
    const colors = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#6366f1', '#ec4899'];
    const index = name ? name.charCodeAt(0) % colors.length : 0;
    return colors[index];
};

export default function OurMember() {
    const swiperRef = useRef(null);
    const [currentSlide, setCurrentSlide] = useState(1);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalSlides, setTotalSlides] = useState(0);

    // Fetch new members from API
    useEffect(() => {
        const fetchNewMembers = async () => {
            try {
                setLoading(true);
                const response = await postAPI('authentication/users/list-new-members', {});
                if (response?.status === 1 && response?.data?.length > 0) {
                    setMembers(response.data);
                    setTotalSlides(response.data.length);
                } else {
                    // Fallback to empty state
                    setMembers([]);
                    setTotalSlides(0);
                }
            } catch (error) {
                console.error('Error fetching new members:', error);
                setMembers([]);
                setTotalSlides(0);
            } finally {
                setLoading(false);
            }
        };

        fetchNewMembers();
    }, []);

    // Initialize Swiper after members are loaded
    useEffect(() => {
        if (!loading && members.length > 0 && swiperRef.current) {
            const swiper = new Swiper('.member-active', {
                modules: [Navigation],
                navigation: {
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev'
                },
                loop: members.length > 3,
                slidesPerView: 3,
                spaceBetween: 30,
                speed: 800,
                 watchOverflow: false,

                breakpoints: {
                    1400: { slidesPerView: 4 },
                    1200: { slidesPerView: 4 },
                    992: { slidesPerView: 3 },
                    768: { slidesPerView: 2 },
                    576: { slidesPerView: 2 },
                    0: { slidesPerView: 1 }
                }
            });

            // Update slide counter when slide changes
            swiper.on('slideChange', () => {
                setCurrentSlide(swiper.realIndex + 1);
            });

            return () => {
                if (swiper) swiper.destroy();
            };
        }
    }, [loading, members]);

    // Get the full image URL
    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        // Check if it's already a full URL
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            return imagePath;
        }
        // Prepend the API base URL
        const apiUrl = getURL()?.API;
        return `${apiUrl}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
    };

    // Get member's full name
    const getMemberName = (member) => {
        const parts = [member.first_name, member.middle_name, member.last_name].filter(Boolean);
        return parts.join(' ') || 'Unknown Member';
    };

    // Get batch info
    const getBatchInfo = (member) => {
        const year = member.passing_year || '';
        const discipline = member.discipline || '';
        if (year && discipline) {
            return `${year} ${discipline}`;
        }
        return year || discipline || 'N/A';
    };

    // Don't render if no members
    if (!loading && members.length === 0) {
        return null;
    }

    return (
        <>
            <section className="new-member-slider py-5">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-9 heading text-center">
                            <h2>Our New Members</h2>
                            <p>Welcome to the newest members of the B E College Ex–Students' Club. We celebrate their inclusion in our growing alumni family and look forward to their active participation in strengthening our community.</p>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-12">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="swiper member-active" ref={swiperRef}>
                                        <div className="swiper-wrapper">
                                            {members.map((member, index) => {
                                                const memberName = getMemberName(member);
                                                const avatarColor = getAvatarColor(memberName);
                                                const imageUrl = getImageUrl(member.image);

                                                return (
                                                    <div className="swiper-slide" key={index}>
                                                        <div className="item">
                                                            <figure>
                                                                {imageUrl ? (
                                                                    <img
                                                                        src={imageUrl}
                                                                        className="img-fluid"
                                                                        alt={memberName}
                                                                        onError={(e) => {
                                                                            // On image load error, replace with avatar
                                                                            e.target.style.display = 'none';
                                                                            e.target.nextSibling.style.display = 'block';
                                                                        }}
                                                                    />
                                                                ) : null}
                                                                <div style={{ display: imageUrl ? 'none' : 'block' }}>
                                                                    <AvatarIcon name={memberName} color={avatarColor} />
                                                                </div>
                                                            </figure>
                                                            <div className="text">
                                                                <span>{member?.designation || 'Member'}</span>
                                                                <h3>{memberName}</h3>
                                                                <ul>
                                                                    <li><strong>Batch:</strong> {getBatchInfo(member)}</li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div className="swiper-navigation">
                                        <div className="swiper-button-prev custom-prev"></div>
                                        <div className="member-counter">
                                            <span id="current-slide">{currentSlide}</span> / <span id="total-slide" style={{ color: '#3b82f6', fontSize: '20px' }}>{totalSlides}</span>
                                        </div>
                                        <div className="swiper-button-next custom-next"></div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}
